import { useCallback, useState } from 'react';

const NOTHING_SELECTED = new Map();

/**
 * The invoices ticked in the month list (JBWEB-1273): their numbers, kept from
 * one page to the next, each with the status it had when it was last seen.
 *
 * `listKey` says which list they were ticked in (`useMonthInvoices`: the month
 * and the filters, not the page). Another list starts with nothing ticked, in
 * the same render, so its first page never shows the ticks of the one before;
 * `null`, the tab left, empties it too.
 *
 * `items` are the invoices on screen. A ticked invoice among them takes the
 * status the list gives it now: a page read again after an import says which
 * ticked invoices are still in error. Those on other pages are not read
 * again: `forgetErrors` stops counting them in error once the api may have
 * fixed them (a retry that found nothing to do, a run that ended).
 * `pageState` is "none", "some" or "all" of the page ticked.
 *
 * `selectMany` and `unselectMany` tick and untick a whole row of the grouped
 * list (JBWEB-1309), its invoices read from the api, kept with the others.
 * The invoices of an unfolded row are on screen too, but not in `items`: the
 * row gives them to `seeInvoices` once read, and again whenever `statuses`
 * changes, so that they follow the same rule as those of the page.
 */
const useInvoiceSelection = (listKey, items) => {
  const [selection, setSelection] = useState({
    listKey,
    statuses: NOTHING_SELECTED,
  });
  if (selection.listKey !== listKey) {
    setSelection({ listKey, statuses: NOTHING_SELECTED });
  }
  const kept =
    selection.listKey === listKey ? selection.statuses : NOTHING_SELECTED;
  const pageItems = items ?? [];

  // In the same render, as the reset above: no render counts a ticked invoice
  // on screen with a status the list no longer gives it.
  const reread = pageItems.filter(
    (invoice) =>
      kept.has(invoice.number) && kept.get(invoice.number) !== invoice.status,
  );
  let statuses = kept;
  if (reread.length > 0) {
    statuses = new Map(kept);
    reread.forEach((invoice) => statuses.set(invoice.number, invoice.status));
    setSelection({ listKey, statuses });
  }

  // Ticks come from clicks, after the render that emptied another list's.
  const change = useCallback(
    (edit) =>
      setSelection((current) => {
        const next = new Map(current.statuses);
        edit(next);
        return { listKey, statuses: next };
      }),
    [listKey],
  );

  const toggle = useCallback(
    (invoice) =>
      change((next) => {
        if (next.has(invoice.number)) next.delete(invoice.number);
        else next.set(invoice.number, invoice.status);
      }),
    [change],
  );

  const tickedOnPage = pageItems.filter((invoice) =>
    statuses.has(invoice.number),
  ).length;
  let pageState = 'some';
  if (tickedOnPage === 0) pageState = 'none';
  else if (tickedOnPage === pageItems.length) pageState = 'all';

  // A page all ticked is unticked; otherwise the rest of it is ticked.
  const togglePage = () =>
    change((next) => {
      pageItems.forEach((invoice) => {
        if (pageState === 'all') next.delete(invoice.number);
        else next.set(invoice.number, invoice.status);
      });
    });

  const selectMany = useCallback(
    (invoices) =>
      change((next) => {
        invoices.forEach((invoice) => next.set(invoice.number, invoice.status));
      }),
    [change],
  );

  const unselectMany = useCallback(
    (numbers) =>
      change((next) => {
        numbers.forEach((number) => next.delete(number));
      }),
    [change],
  );

  // Changes nothing, not even the state object, when the statuses match.
  const seeInvoices = useCallback(
    (invoices) =>
      setSelection((current) => {
        const stale = invoices.filter(
          (invoice) =>
            current.statuses.has(invoice.number) &&
            current.statuses.get(invoice.number) !== invoice.status,
        );
        if (stale.length === 0) return current;
        const next = new Map(current.statuses);
        stale.forEach((invoice) => next.set(invoice.number, invoice.status));
        return { listKey: current.listKey, statuses: next };
      }),
    [],
  );

  const clear = useCallback(
    () => setSelection({ listKey, statuses: NOTHING_SELECTED }),
    [listKey],
  );

  // `forgotten` numbers, or all of them, are no longer known to be in error.
  // Those on screen take their status back from the list at the next render.
  const forgetErrors = useCallback(
    (forgotten) =>
      setSelection((current) => {
        const next = new Map(current.statuses);
        (forgotten ?? [...next.keys()]).forEach((number) => {
          if (next.get(number) === 'error') next.set(number, null);
        });
        return { listKey: current.listKey, statuses: next };
      }),
    [],
  );

  const numbers = [...statuses.keys()];
  const errorNumbers = numbers.filter(
    (number) => statuses.get(number) === 'error',
  );

  return {
    numbers,
    errorNumbers,
    isSelected: (number) => statuses.has(number),
    toggle,
    pageState,
    togglePage,
    selectMany,
    unselectMany,
    seeInvoices,
    statuses,
    clear,
    forgetErrors,
  };
};

export default useInvoiceSelection;
